import { startPerformanceMeasure } from '@vend/utilities'
import { useCallback } from 'react'
import { STATES } from '../../../../../../lib/routes'
import { outletsStore } from '../../../../factories/database/outlets-store'
import { isValidCustomer } from '../../../../functions/sale'
import {
  useCustomerManager,
  useIsCalculatingSale,
  useTrainingModeManager,
  useWorkflowsManager,
} from '../../../../ng-compat/hooks'
import { $state } from '../../../../ng-compat/imports'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import {
  selectLineItems,
  selectSaleCustomer,
  setFulfilmentOutletNamesById,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import {
  finalisingSale,
  finalisingSaleComplete,
  workflowsComplete,
  workflowsStarted,
} from '../../../../state/slices/uiStateSlice'
import { useCrossLocationFulfilmentGate } from '../../sale-line-item/fulfilment/OutletSelect'
import { useCanTransitionToPaymentScreen } from './use-can-transition-to-payment-screen'

const getFulfilmentOutletIds = (
  lineItems: ReturnType<typeof selectLineItems>
) =>
  Array.from(
    new Set(
      lineItems
        .map(lineItem => lineItem.fulfilmentOutletId)
        .filter((outletId): outletId is string => Boolean(outletId))
    )
  )

const loadFulfilmentOutletNamesById = async (outletIds: string[]) => {
  try {
    const outlets = await Promise.all(
      outletIds.map(outletId => outletsStore.get(outletId, true))
    )

    return outlets.reduce<Record<string, string>>(
      (namesById, outlet, index) => {
        const outletId = outletIds[index]

        if (outlet?.name) {
          namesById[outletId] = outlet.name
        }

        return namesById
      },
      {}
    )
  } catch {
    return {}
  }
}

export const useTransitionToPaymentScreen = (
  lspayQuickPaySelected?: boolean
) => {
  const dispatch = useAppDispatch()
  const trainingModeManager = useTrainingModeManager()
  const customerManager = useCustomerManager()
  const workflowsManager = useWorkflowsManager()
  const customer = useAppSelector(selectSaleCustomer)
  const lineItems = useAppSelector(selectLineItems)
  const canTransitionToPaymentScreen = useCanTransitionToPaymentScreen()
  const isCalculating = useIsCalculatingSale()
  const { canUseCrossLocationFulfilment } = useCrossLocationFulfilmentGate()

  return useCallback(async () => {
    if (!canTransitionToPaymentScreen || isCalculating) {
      return
    }

    dispatch(finalisingSale())

    if (!trainingModeManager.trainingModeEnabled) {
      // time how long it takes to tender a sale
      startPerformanceMeasure('sale-process')
      // start timing how long it takes to initiate a sale with a payment type
      startPerformanceMeasure('select-payment-type-process')
    }

    const customerRefreshPromise = isValidCustomer(customer)
      ? customerManager.refreshCustomer(customer)
      : Promise.resolve()

    try {
      await customerRefreshPromise

      dispatch(workflowsStarted())
      const shouldProceed = await workflowsManager.readyForPayment({
        element: document.getElementById('wr-pay-button')!,
        placement: 'top',
      })

      if (shouldProceed) {
        const fulfilmentOutletIds = getFulfilmentOutletIds(lineItems)
        const fulfilmentOutletNamesById =
          canUseCrossLocationFulfilment && fulfilmentOutletIds.length > 0
            ? await loadFulfilmentOutletNamesById(fulfilmentOutletIds)
            : {}

        dispatch(setFulfilmentOutletNamesById(fulfilmentOutletNamesById))

        $state.go(STATES.PAYMENT, {
          lspayQuickPaySelected,
        })
      }
    } finally {
      dispatch(workflowsComplete())
      dispatch(finalisingSaleComplete())
    }
  }, [
    canTransitionToPaymentScreen,
    canUseCrossLocationFulfilment,
    customer,
    customerManager,
    dispatch,
    isCalculating,
    lineItems,
    lspayQuickPaySelected,
    trainingModeManager.trainingModeEnabled,
    workflowsManager,
  ])
}
